import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/auth";

import { getFirebaseBackend } from "../../authUtils";

/**
 * Empleado autenticado y su rol.
 *
 * La pertenencia a `empleados` sólo se comprobaba al iniciar sesión
 * (`views/account/login.vue`): una vez dentro, quitar a alguien de la colección
 * no lo expulsaba mientras su sesión de Firebase siguiera viva. Este módulo
 * cachea el documento para que el guard de rutas pueda revalidarlo en cada
 * navegación sin volver a leer Firestore cada vez.
 *
 * Aviso: esto es control de interfaz. La barrera real son las reglas de
 * seguridad de Firestore, que no viven en este repositorio: mientras permitan
 * escribir a cualquier usuario autenticado, un usuario de la app móvil (mismo
 * proyecto de Firebase) puede tocar los datos sin pasar por este panel.
 */

export const ROL_ADMIN = "admin";
export const ROL_LECTOR = "lector";

export const state = {
  documento: null,
  correoCargado: null,
  cargando: false,
};

export const mutations = {
  SET_EMPLEADO(state, { documento, correo }) {
    state.documento = documento;
    state.correoCargado = correo;
  },
  SET_CARGANDO(state, valor) {
    state.cargando = valor;
  },
  LIMPIAR(state) {
    state.documento = null;
    state.correoCargado = null;
  },
};

export const getters = {
  /** Los documentos que ya existían no tienen `rol`: se asumen administradores. */
  rol: (state) => (state.documento ? state.documento.rol || ROL_ADMIN : null),
  esAdmin: (state, getters) => getters.rol === ROL_ADMIN,
  puedeEditar: (state, getters) => getters.rol === ROL_ADMIN,
  nombre: (state) => (state.documento ? state.documento.nombre || state.documento.email : ""),
  autorizado: (state) => !!state.documento,
};

export const actions = {
  /**
   * Devuelve el documento del empleado autenticado, o null si no lo hay.
   * Reutiliza la caché mientras se trate del mismo correo.
   */
  async cargar({ commit, state }, { force = false } = {}) {
    // Espera a que Firebase restaure la sesión; en un recargado el guard corre
    // antes y `currentUser` sería null, expulsando a un empleado válido.
    await getFirebaseBackend().esperarAuthListo();
    const usuario = firebase.auth().currentUser;
    const correo = usuario && usuario.email ? usuario.email.toLowerCase() : null;

    if (!correo) {
      commit("LIMPIAR");
      return null;
    }
    if (!force && state.correoCargado === correo) return state.documento;

    commit("SET_CARGANDO", true);
    try {
      const snap = await firebase
        .firestore()
        .collection("empleados")
        .where("email", "==", correo)
        .limit(1)
        .get();

      const documento = snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
      commit("SET_EMPLEADO", { documento, correo });
      return documento;
    } catch (err) {
      // Un fallo de red no debe cerrar la sesión de quien ya estaba dentro.
      console.warn("[empleado] no se pudo verificar el empleado:", err);
      commit("SET_CARGANDO", false);
      return state.documento;
    } finally {
      commit("SET_CARGANDO", false);
    }
  },

  limpiar({ commit }) {
    commit("LIMPIAR");
  },
};
